import { formatCurrency } from "helpers/utils";
import type { IPagamentoLoja, MetodoPagamento } from "interfaces/loja";

export type ResumoValoresPedido = {
  valorPedido: number;
  valorCobrado: number;
  valorJuros: number;
  temJuros: boolean;
  parcelas: number;
  valorParcela: number | null;
};

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function calcularResumoValoresPedido(
  valorPedido: number | string,
  pagamentos?: IPagamentoLoja[] | null,
): ResumoValoresPedido {
  const vp = roundMoney(Number(valorPedido) || 0);
  const pg =
    pagamentos?.find(
      (p) =>
        p.status === "PAGO" ||
        p.status === "AUTORIZADO" ||
        p.status === "ESTORNADO" ||
        p.status === "PENDENTE"
    ) ?? pagamentos?.[0];

  if (!pg) {
    return {
      valorPedido: vp,
      valorCobrado: vp,
      valorJuros: 0,
      temJuros: false,
      parcelas: 1,
      valorParcela: null,
    };
  }

  const metodo = String(pg.metodo ?? "").toUpperCase();
  const isCartao =
    metodo === "CARTAO_CREDITO" || metodo === "CARTAO_DEBITO";

  const parcelasRaw = Number(pg.parcelas);
  const parcelas =
    Number.isFinite(parcelasRaw) && parcelasRaw >= 1
      ? Math.min(24, Math.floor(parcelasRaw))
      : 1;

  let valorCobrado = vp;
  const valorPg = Number(pg.valor);
  if (Number.isFinite(valorPg) && valorPg > 0) {
    valorCobrado = roundMoney(valorPg);
  }

  const jurosPersistido = Number(pg.valorJuros);
  let valorJuros: number;
  if (Number.isFinite(jurosPersistido) && jurosPersistido >= 0) {
    valorJuros = roundMoney(jurosPersistido);
    if (jurosPersistido > 0) {
      valorCobrado = roundMoney(vp + valorJuros);
    }
  } else {
    valorJuros = roundMoney(Math.max(0, valorCobrado - vp));
  }
  const temJuros = valorJuros >= 0.01;

  let valorParcela: number | null = null;
  const parcelaPersistida = Number(pg.valorParcela);
  if (Number.isFinite(parcelaPersistida) && parcelaPersistida > 0) {
    valorParcela = roundMoney(parcelaPersistida);
  } else if (isCartao) {
    valorParcela =
      parcelas > 1
        ? roundMoney(valorCobrado / parcelas)
        : roundMoney(valorCobrado);
  }

  return {
    valorPedido: vp,
    valorCobrado,
    valorJuros,
    temJuros,
    parcelas,
    valorParcela,
  };
}

export function calcularResumoValoresPagamento(
  valorPedido: number | string,
  pg: IPagamentoLoja,
): ResumoValoresPedido {
  return calcularResumoValoresPedido(valorPedido, [pg]);
}

export function textoParcelasDetalhado(
  metodo: MetodoPagamento | string,
  resumo: ResumoValoresPedido,
): string | null {
  const m = String(metodo).toUpperCase();
  if (m !== "CARTAO_CREDITO" && m !== "CARTAO_DEBITO") return null;
  if (resumo.parcelas <= 1) return "À vista (1x)";
  const vp =
    resumo.valorParcela ?? roundMoney(resumo.valorCobrado / resumo.parcelas);
  const sufixo = resumo.temJuros ? "com juros" : "sem juros";
  return `Em ${resumo.parcelas} parcelas (${formatCurrency(vp)} cada) — ${sufixo}`;
}
